import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Shield, BookOpen, ExternalLink, Mail } from 'lucide-react';

const EthicsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="mb-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-amber-500/10 border-2 border-amber-500 rounded-xl flex items-center justify-center">
                <Shield className="w-10 h-10 text-amber-500" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              Ethics, Disclaimers & Safety
            </h1>
            <p className="text-xl text-gray-400">
              Fiction. Satire. Conceptual Education.
            </p>
          </div>

          {/* Primary Disclaimer Banner */}
          <div className="bg-amber-950/50 border-2 border-amber-600 rounded-lg p-8 mb-12">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-amber-400 mb-3">Primary Disclaimer</h2>
                <p className="text-gray-200 text-lg leading-relaxed mb-4">
                  <strong>AI Operator From Hell (AIOFH) is a work of fiction and satire.</strong>
                </p>
                <p className="text-gray-300 leading-relaxed">
                  All stories, characters, incidents, tools, and scenarios presented on this website are{' '}
                  <strong className="text-gray-100">entirely fictional</strong>. Any resemblance to real systems,
                  persons, companies, networks, or events is <strong className="text-gray-100">purely coincidental
                    and unintended</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* What This Site Is / Is NOT */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* What It IS */}
            <div className="bg-teal-950/30 border border-teal-500/50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-teal-400 mb-4 flex items-center gap-2">
                <span className="text-2xl">✅</span> What This Site Is
              </h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-1">•</span>
                  <span><strong>Satirical Fiction</strong> inspired by sysadmin culture and tech work</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-1">•</span>
                  <span><strong>Educational (Conceptual Level)</strong> teaching high-level security principles</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-1">•</span>
                  <span><strong>Entertainment</strong> for technical audiences who work in IT and security</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-1">•</span>
                  <span><strong>Clearly Labeled</strong> with prominent fiction notices and disclaimers</span>
                </li>
              </ul>
            </div>

            {/* What It IS NOT */}
            <div className="bg-red-950/30 border border-red-500/50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2">
                <span className="text-2xl">❌</span> What This Site Is NOT
              </h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Not Operational Instructions</strong> - No step-by-step exploit tutorials</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Not Encouraging Illegal Activity</strong> - Does not condone unauthorized access</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Not Based on Real Incidents</strong> - Stories are creative fiction</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Not Professional Education</strong> - Use OWASP, SANS, NIST for real training</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Content Safety Policy */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-100 mb-6 flex items-center gap-3">
              <Shield className="w-8 h-8 text-teal-500" />
              Content Safety Policy
            </h2>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-100 mb-2">1. No Operational Details</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Stories reference tools and concepts at a <strong className="text-gray-300">high level only</strong>.
                  We mention that tools exist, not how to use them maliciously.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-100 mb-2">2. Fictional and Absurd Context</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Technical content is presented with servers achieving sentience, impossible scenarios,
                  and theatrical solutions that clearly mark this as fiction.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-100 mb-2">3. High-Level Concepts Only</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Tutorials explain <strong className="text-gray-300">why</strong> things matter,
                  not <strong className="text-gray-300">how</strong> to exploit them.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-100 mb-2">4. Prominent Disclaimers</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Every page includes ethics reminders, fiction labels, and links to legitimate learning resources.
                </p>
              </div>
            </div>
          </section>

          {/* Learning Resources */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-100 mb-6 flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-teal-500" />
              For Real Security Education
            </h2>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Want to learn security professionally and ethically? These are the legitimate resources:
            </p>

            <div className="space-y-6">
              {/* Official Training */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-100 mb-4">Industry Organizations</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <a
                    href="https://owasp.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">OWASP</div>
                      <div className="text-xs text-gray-400">Free resources, Top 10 vulnerabilities</div>
                    </div>
                  </a>
                  <a
                    href="https://sans.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">SANS Institute</div>
                      <div className="text-xs text-gray-400">Professional training, certifications</div>
                    </div>
                  </a>
                  <a
                    href="https://nist.gov/cyberframework"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">NIST</div>
                      <div className="text-xs text-gray-400">Cybersecurity Framework, guidelines</div>
                    </div>
                  </a>
                  <a
                    href="https://krebsonsecurity.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">Krebs on Security</div>
                      <div className="text-xs text-gray-400">Security news and analysis</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Hands-On Practice */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-100 mb-4">Hands-On Practice (Legal & Authorized)</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <a
                    href="https://hackthebox.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">HackTheBox</div>
                      <div className="text-xs text-gray-400">CTF platform, realistic challenges</div>
                    </div>
                  </a>
                  <a
                    href="https://tryhackme.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">TryHackMe</div>
                      <div className="text-xs text-gray-400">Beginner-friendly security training</div>
                    </div>
                  </a>
                  <a
                    href="https://pentesterlab.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">PentesterLab</div>
                      <div className="text-xs text-gray-400">Web penetration testing exercises</div>
                    </div>
                  </a>
                  <a
                    href="https://overthewire.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-gray-100 group-hover:text-teal-500 transition-colors">OverTheWire</div>
                      <div className="text-xs text-gray-400">Wargames for learning security</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* About BOFH */}
          <section className="mb-12">
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-100 mb-3">About BOFH and Inspiration</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                <strong>BOFH (Bastard Operator From Hell)</strong> is a classic satirical series created by Simon Travaglia,
                chronicling the adventures of a sysadmin with... flexible ethics.
              </p>
              <p className="text-gray-400 leading-relaxed text-sm">
                AIOFH is inspired by BOFH but is a separate, independent work focused on AI/modern tech themes
                with explicit ethical boundaries. We're not affiliated with or endorsed by Simon Travaglia,
                but we acknowledge our debt to the original.
              </p>
            </div>
          </section>

          {/* Reporting & Contact */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-100 mb-6 flex items-center gap-3">
              <Mail className="w-8 h-8 text-amber-500" />
              Reporting Concerns
            </h2>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                If you believe any content on this site crosses ethical or legal boundaries, contact us immediately:
              </p>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-5 h-5 text-amber-500" />
                  <span className="font-bold text-gray-100">Email:</span>
                  <a href="mailto:ethics@aoifh.com" className="text-amber-500 hover:text-amber-400 transition-colors">
                    ethics@aoifh.com
                  </a>
                </div>
                <div className="text-sm text-gray-400">
                  Subject Line: "Ethics Concern - [Brief Description]"
                </div>
              </div>
              <div className="text-sm text-gray-400 space-y-1">
                <div>• Initial acknowledgment: 48 hours</div>
                <div>• Full review: 7 days</div>
                <div>• Action (if needed): Immediate content removal or revision</div>
              </div>
            </div>
          </section>

          {/* For Parents & Educators */}
          <section className="mb-12">
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-100 mb-4">For Parents and Educators</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-200 mb-2">Is AIOFH appropriate for students?</h4>
                  <p className="text-gray-400 text-sm leading-relaxed mb-3">
                    This site contains satirical humor about technology and conceptual security education.
                    The humor is occasionally dark and sarcastic.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-teal-500 font-bold mb-2">Recommended for:</div>
                      <ul className="text-gray-400 space-y-1">
                        <li>• Ages 16+ (or mature teens)</li>
                        <li>• Computer science students</li>
                        <li>• IT/security learners</li>
                      </ul>
                    </div>
                    <div>
                      <div className="text-red-500 font-bold mb-2">Not recommended for:</div>
                      <ul className="text-gray-400 space-y-1">
                        <li>• Young children</li>
                        <li>• Those seeking exploit tutorials</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Final Statement */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-100 mb-4">Final Statement</h3>
            <p className="text-gray-300 leading-relaxed mb-6">
              <strong>AI Operator From Hell is fiction, satire, and conceptual education.</strong>
            </p>
            <p className="text-gray-400 leading-relaxed mb-4">
              We believe technology culture deserves celebration, sysadmins deserve recognition,
              and everyone deserves a good laugh—all while maintaining clear ethical boundaries.
            </p>
            <div className="border-t border-gray-700 pt-6 mt-6">
              <p className="text-gray-400 italic mb-2">
                "Documented. Filed. Ready for ethical deployment."
              </p>
              <p className="text-gray-500 text-sm">
                — The Operator
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 pt-8 border-t border-gray-800 flex items-center justify-center gap-6">
            <Link to="/" className="btn btn-primary">
              Back to Homepage
            </Link>
            <Link to="/tutorials" className="btn btn-secondary">
              Read Tutorials
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EthicsPage;
